import React from 'react';
import clsx from 'clsx';
import { LockClosedIcon } from '@heroicons/react/24/outline';

import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import Tooltip from '@dashboard/components/Tooltip';

/** The actions column: a bordered card with grey group headings and a hairline between groups. */
export const OrderActionsColumn = ({ children }) => (
  <div
    className="flex flex-col w-full rounded-lg border border-gray-200 dark:border-border px-4 divide-y divide-gray-200 dark:divide-border"
    data-testid="order-actions"
  >
    {children}
  </div>
);

export const ActionGroup = ({ title, children }) => (
  <div className="w-full flex flex-col gap-y-2 py-4">
    <div className="text-sm font-medium text-gray-500 dark:text-muted-foreground">{title}</div>
    {children}
  </div>
);

export const actionClass = (off, extra) =>
  clsx('w-full flex gap-x-2 items-center', { 'pointer-events-none opacity-50': off }, extra);

const runWithoutFollowing = onClick => event => {
  event.preventDefault();
  onClick?.(event);
};

/**
 * One action. Every action is always rendered; `off` greys it out and `title` says why in a tooltip.
 * Renders a Link to `to` (internal, or `blank` for a new tab) or an onClick link.
 */
export const Action = ({ icon: Icon, label, to, blank, onClick, off, title, lock, loading }) => {
  const inert = off || loading;
  const content = (
    <>
      <Icon className="w-4 h-4" />
      {label}
      {lock && <LockClosedIcon className="w-3.5 h-3.5 ml-auto" />}
      {loading && <Spinner width="24px" height="24px" />}
    </>
  );
  // pointer-events-none only stops the mouse, so a greyed action drops its href and handler too:
  // an anchor without href takes no focus, so neither Tab nor Enter can reach it.
  const link = inert ? (
    <Link aria-disabled="true" className={actionClass(true)}>
      {content}
    </Link>
  ) : (
    <Link
      to={to ?? '#'}
      blank={blank}
      onClick={to ? onClick : runWithoutFollowing(onClick)}
      className={actionClass(false)}
    >
      {content}
    </Link>
  );
  return off && title ? (
    // Anchored to the label, not the row, so the tooltip opens beside the words; a lock needs the row.
    <Tooltip side="right" label={title} triggerClassName={lock ? 'w-full' : 'w-fit'}>
      {link}
    </Tooltip>
  ) : (
    link
  );
};
